import { Navigate, NavLink, Outlet, useNavigate } from "react-router-dom";
import {
  LayoutDashboard,
  Megaphone,
  Radio as RadioIcon,
  Tv,
  Music,
  Vote,
  MessageSquareWarning,
  Instagram,
  BellRing,
  Library,
  ShieldCheck,
  Settings,
  LogOut,
  Mic,
  ChevronLeft,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { ADMIN } from "@/constants/testIds";
import { Toaster } from "@/components/ui/sonner";

const NAV_ITEMS = [
  { to: "/admin", label: "Dashboard", icon: LayoutDashboard, testId: ADMIN.nav.dashboard, end: true },
  { to: "/admin/anuncios", label: "Anúncios", icon: Megaphone, testId: ADMIN.nav.ads },
  { to: "/admin/radio", label: "Rádio", icon: RadioIcon, testId: ADMIN.nav.radio },
  { to: "/admin/episodios", label: "Episódios", icon: Library, testId: "admin-nav-episodes" },
  { to: "/admin/tv", label: "TV & Vídeos", icon: Tv, testId: ADMIN.nav.tv },
  { to: "/admin/musicas", label: "Playlist", icon: Music, testId: "admin-nav-music" },
  { to: "/admin/enquetes", label: "Enquetes", icon: Vote, testId: ADMIN.nav.polls },
  { to: "/admin/mensagens", label: "Mensagens", icon: MessageSquareWarning, testId: ADMIN.nav.citizens },
  { to: "/admin/instagram", label: "Instagram", icon: Instagram, testId: "admin-nav-instagram" },
  { to: "/admin/notificacoes", label: "Notificações", icon: BellRing, testId: "admin-nav-notifications" },
  { to: "/admin/configuracoes", label: "Configurações", icon: Settings, testId: "admin-nav-settings" },
];

const AdminLayout = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  if (user === null) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[var(--fi-bg)] text-[var(--fi-blue)]">
        Verificando sessão…
      </div>
    );
  }
  if (user === false) return <Navigate to="/admin/login" replace />;

  const doLogout = async () => {
    await logout();
    navigate("/admin/login");
  };

  return (
    <div className="min-h-screen flex flex-col md:flex-row bg-[var(--fi-bg)]">
      {/* Sidebar */}
      <aside
        className="md:w-64 md:min-h-screen flex-shrink-0"
        style={{ background: "var(--fi-blue)" }}
      >
        <div className="p-5 border-b border-white/10 flex items-center justify-between md:justify-start gap-3">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl fi-gradient flex items-center justify-center">
              <div className="w-[34px] h-[34px] rounded-[10px] bg-[var(--fi-blue)] flex items-center justify-center">
                <Mic className="w-5 h-5 text-white" strokeWidth={2.4} />
              </div>
            </div>
            <div className="leading-none">
              <div className="font-display text-white text-xl font-extrabold uppercase">
                Fala Itapira
              </div>
              <div className="text-[10px] tracking-[0.28em] uppercase text-white/50 font-bold mt-0.5">
                Admin
              </div>
            </div>
          </div>
        </div>

        <nav className="p-3 flex md:flex-col gap-1 overflow-x-auto md:overflow-visible no-scrollbar">
          {NAV_ITEMS.map(({ to, label, icon: Icon, testId, end }) => (
            <NavLink
              key={to}
              to={to}
              end={end}
              data-testid={testId}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-semibold whitespace-nowrap transition-colors ${
                  isActive
                    ? "bg-white/15 text-white"
                    : "text-white/70 hover:text-white hover:bg-white/5"
                }`
              }
            >
              <Icon className="w-4 h-4" />
              <span>{label}</span>
            </NavLink>
          ))}
        </nav>

        <div className="p-3 md:mt-4 border-t border-white/10 flex flex-col gap-1">
          <NavLink
            to="/"
            className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-semibold text-white/60 hover:text-white hover:bg-white/5 transition-colors"
          >
            <ChevronLeft className="w-4 h-4" />
            <span>Ir ao app público</span>
          </NavLink>
          <NavLink
            to="/politica-de-privacidade"
            data-testid="admin-privacy-link"
            className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-semibold text-white/60 hover:text-white hover:bg-white/5 transition-colors"
          >
            <ShieldCheck className="w-4 h-4" />
            <span>Política de Privacidade</span>
          </NavLink>
          <button
            data-testid={ADMIN.logout}
            onClick={doLogout}
            className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-semibold text-white/60 hover:text-white hover:bg-white/5 transition-colors text-left w-full"
          >
            <LogOut className="w-4 h-4" />
            <span>Sair</span>
          </button>
        </div>
      </aside>

      {/* Main */}
      <main className="flex-1 min-w-0">
        <div className="max-w-6xl mx-auto p-4 md:p-8">
          <Outlet />
        </div>
      </main>
      <Toaster richColors position="top-center" />
    </div>
  );
};

export default AdminLayout;
